import { GridFilterModel, GridSortModel } from '@mui/x-data-grid-pro';
import { Dispatch, SetStateAction, useState } from 'react';
import UserGridActions from './user-grid-actions/user-grid-actions';
import { useQueryClient } from '@tanstack/react-query';
import { UserKeys } from '../../api/users-api';
import {
  UserRoles,
  isAbleToSeeUIElement,
} from '../../utils/roles/client-roles';
import { gPanel_Tier } from '../../../types';
import SharedGridStateIconButton from '../../shared/shared-grid-state-icon-button-latest';
import { ModuleList } from '../../shared/analytics/module-list';
import SharedToolbar from '../../shared/shared-toolbar-latest';
import { useNavigate } from 'react-router-dom';
import { pagesConfig } from '../../shared/pages-config';
import CreateButton from '../../shared/buttons/create-button';
import SharedIconButton from '../../shared/buttons/shared-icon-button';

import Tooltip from '@mui/material/Tooltip';
import IconButton from '@mui/material/IconButton';
import Menu from '@mui/material/Menu';
import Tune from '@mui/icons-material/Tune';

interface UsersIndexedGridToolbarProps {
  paginationModel: {
    pageSize: number;
    page: number;
  };
  filterModel: GridFilterModel;
  setFilterModel: Dispatch<SetStateAction<GridFilterModel>>;
  gridFilterModel: GridFilterModel;
  setGridFilterModel: Dispatch<SetStateAction<GridFilterModel>>;
  sortModel: GridSortModel;
  userRoles: string[];
  selectedRows: string[];
  setSelectedRows: Dispatch<SetStateAction<string[]>>;
  changeLogRoles: string[];
  currentTier: gPanel_Tier;
  setOpenQuickReports: (open: boolean) => void;
}

export default function UsersIndexedGridToolbar({
  paginationModel,
  filterModel,
  sortModel,
  userRoles,
  selectedRows,
  setSelectedRows,
  changeLogRoles,
  currentTier,
  setOpenQuickReports,
}: UsersIndexedGridToolbarProps) {
  const queryClient = useQueryClient();
  const navigate = useNavigate();
  const [anchorEl, setAnchorEl] = useState<null | HTMLElement>(null);
  const open = Boolean(anchorEl);
  const handleClick = (event: React.MouseEvent<HTMLButtonElement>) => {
    setAnchorEl(event.currentTarget);
  };
  const handleClose = () => {
    setAnchorEl(null);
  };

  return (
    <SharedToolbar
      quickFilterPlaceholder={'Search By Email...'}
      quickFilterDebounceMs={300}
      primaryButtons={
        <CreateButton
          disabled={
            !isAbleToSeeUIElement(userRoles, UserRoles.CreateUser.value)
          }
          onClick={() =>
            navigate(
              `/${pagesConfig.users.link}/${pagesConfig.createUser.link}`
            )
          }
          label="Create User"
        />
      }
      secondaryButtons={
        <>
          <Tooltip
            arrow
            title={
              currentTier === 'essentials'
                ? `At least Starter Tier is required to access this tool.`
                : 'Actions'
            }
          >
            <span>
              <IconButton
                disabled={
                  selectedRows.length === 0 || currentTier === 'essentials'
                }
                aria-controls={open ? 'actions-menu' : undefined}
                aria-haspopup="true"
                aria-expanded={open ? 'true' : undefined}
                onClick={handleClick}
              >
                <Tune />
              </IconButton>
            </span>
          </Tooltip>
          <Menu
            id="actions-menu"
            anchorEl={anchorEl}
            open={open}
            onClose={handleClose}
            transformOrigin={{ horizontal: 'right', vertical: 'top' }}
            anchorOrigin={{ horizontal: 'right', vertical: 'bottom' }}
          >
            <UserGridActions
              selectedRows={selectedRows}
              setSelectedRows={setSelectedRows}
              userRoles={userRoles}
              changeLogRoles={changeLogRoles}
            />
          </Menu>
          <SharedIconButton
            variant="quickReport"
            onClick={() => {
              setOpenQuickReports(true);
            }}
            disabled={selectedRows.length === 0}
            tooltipLabel="Quick Reports"
          />

          <SharedIconButton
            variant="refresh"
            onClick={() => {
              void queryClient.invalidateQueries({
                queryKey: [
                  UserKeys.UsersIndexedPage,
                  paginationModel.page,
                  paginationModel.pageSize,
                  filterModel,
                  sortModel,
                ],
              });
            }}
            tooltipLabel="Refresh"
          />
          <SharedGridStateIconButton
            gridStateKey="userManagementIndexedGridStates"
            module={ModuleList.modules.userManagement.name}
            section={ModuleList.modules.userManagement.sections.userDetails}
          />
        </>
      }
    />
  );
}
